class ApiError extends Error {
  constructor(status, detail) {
    super(detail);
    this.response = { status, data: { detail } };
  }
}

function parseBaseUrl(baseUrl) {
  const raw = (baseUrl ?? process.env.REACT_APP_BACKEND_URL ?? "").trim();
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
}

async function requestJson(baseUrl, path, options = {}) {
  const res = await fetch(`${baseUrl}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  const text = await res.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }

  if (!res.ok) {
    const detail =
      (payload && typeof payload === "object" && payload.detail) ||
      (typeof payload === "string" && payload) ||
      `Request failed (${res.status})`;
    throw new ApiError(res.status, detail);
  }
  return payload;
}

export function createBackendApi(config = {}) {
  const baseUrl = parseBaseUrl(config.baseUrl);
  const apiPrefix = `${baseUrl}/api`;

  return {
    mode: "backend",
    coachProviders: ["engine"],

    async health() {
      return requestJson(baseUrl, "/api/health");
    },

    async getGames() {
      return requestJson(baseUrl, "/api/games");
    },

    async getGame(gameId) {
      return requestJson(baseUrl, `/api/games/${gameId}`);
    },

    async createGame(input) {
      return requestJson(baseUrl, "/api/games", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },

    async importPgn(input) {
      return requestJson(baseUrl, "/api/games/import-pgn", {
        method: "POST",
        body: JSON.stringify(input),
      });
    },

    async deleteGame(gameId) {
      return requestJson(baseUrl, `/api/games/${gameId}`, { method: "DELETE" });
    },

    async move(gameId, request) {
      return requestJson(baseUrl, `/api/games/${gameId}/move`, {
        method: "POST",
        body: JSON.stringify(request),
      });
    },

    async undo(gameId) {
      return requestJson(baseUrl, `/api/games/${gameId}/undo`, {
        method: "POST",
      });
    },

    async getHud(gameId, moveIndex) {
      const query = typeof moveIndex === "number" ? `?move_index=${moveIndex}` : "";
      return requestJson(baseUrl, `/api/games/${gameId}/hud${query}`);
    },

    async exportPgn(gameId) {
      return requestJson(baseUrl, `/api/games/${gameId}/pgn`);
    },

    async getSettings() {
      return requestJson(baseUrl, "/api/settings");
    },

    async updateSettings(update) {
      return requestJson(baseUrl, "/api/settings", {
        method: "PUT",
        body: JSON.stringify(update),
      });
    },

    async coach(gameId, request) {
      return requestJson(baseUrl, `/api/games/${gameId}/coach`, {
        method: "POST",
        body: JSON.stringify(request),
      });
    },

    apiPrefix,
  };
}
